// src/components/Dashboard/Dashboard.js
import { AnimatePresence, motion } from 'framer-motion';
import { Edit, Search, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import employeeIcon from '../../assets/employee.png';
import { useScreenLogging } from '../../hooks/useScreenLogging';

// Assuming these are your custom UI components from your project
import { Typography } from '@mui/material';
import Button from '../../components/ui/button.js';
import Input from '../../components/ui/input.js';

// Import ALL your separate components with corrected explicit extensions
import EmployeeInfoTable from './EmployeeInfoTable.jsx'; // <--- IMPORT NEW COMPONENT
import MapComponent from './MapComponent.jsx';
import SiteInformationCard from './SiteInformationCard.js';
import SiteScheduleComponent from './SiteScheduleComponent.jsx';
import TrackRidesComponent from './TrackRidesComponent.jsx'; // Import TrackRidesComponent
// Removed import for TestApiComponent

// Import the new API utility
import { getShiftRoster } from '../ShiftManagement/ShiftShedule_Api_service.js'; // Import getShiftRoster for fetching notes
import { dashboardApi } from './dashboardApi.js'; // Corrected relative path

// Import mock data from the new file
import { mockAbsentShifts, mockLiveRides } from './mockData.js'; // mockCurrentStats removed as it's no longer needed

// Import CurrentStatsCard
import CurrentStatsCard from './CurrentStatsCard.js';

// Styles and utilities
const glassmorphism =
  'bg-white/20 backdrop-blur-md rounded-xl border border-white/10 shadow-lg';
const greenTheme = 'text-gray-900'; // Changed to a darker text color
const combineClasses = (...classes) => classes.filter(Boolean).join(' ');
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

// Specific style for the Site Info panel (lighter, more translucent blue, and black text)
const rightPanelStyle = {
  borderRadius: '12px',
  border: '1px solid #FFF',
  background:
    'radial-gradient(184.58% 152.09% at 136.24% -51.3%, rgba(10, 128, 137, 0.09) 0%, rgba(255, 255, 255, 0.72) 100%), linear-gradient(0deg, rgba(230, 230, 230, 0.60) 0%, #E0F2F7 100%)',
  boxShadow:
    '0px 5px 20.1px 0px rgba(255,255,255,0.10), 0px 15px 30px 0px rgba(0,0,0,0.10), 0px 4px 25px 0px rgba(255,255,255,0.35) inset',
  backdropFilter: 'blur(30px)',
};

// Modal for adding/editing offices
const OfficeModal = ({ isOpen, onClose, office, onSave }) => {
  const [name, setName] = useState(office ? office.name : '');
  const [address, setAddress] = useState(office ? office.address : '');
  const [lat, setLat] = useState(office ? office.lat : '');
  const [lng, setLng] = useState(office ? office.lng : '');
  const [numGuards, setNumGuards] = useState(office ? office.numGuards : 0);
  const [supervisors, setSupervisors] = useState(
    office ? office.supervisors : 0
  );
  const [employeesCount, setEmployeesCount] = useState(
    office ? office.employees : 0
  );

  const handleSubmit = e => {
    e.preventDefault();
    onSave({
      ...office,
      name,
      address,
      lat: parseFloat(lat),
      lng: parseFloat(lng),
      numGuards: parseInt(numGuards),
      supervisors: parseInt(supervisors),
      employees: parseInt(employeesCount),
      id: office ? office.id : Date.now().toString(),
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        className={combineClasses(
          glassmorphism,
          'p-6 w-full max-w-md space-y-4'
        )}
      >
        <h2 className="text-2xl font-bold text-green-400">
          {office ? 'Edit Office' : 'Add New Office'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="officeName"
              className="block text-gray-700 text-sm font-medium mb-1"
            >
              Name
            </label>
            <Input
              id="officeName"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className={combineClasses(
                glassmorphism,
                'w-full placeholder:text-gray-500'
              )}
              required
            />
          </div>
          <div>
            <label
              htmlFor="officeAddress"
              className="block text-gray-700 text-sm font-medium mb-1"
            >
              Address
            </label>
            <Input
              id="officeAddress"
              type="text"
              value={address}
              onChange={e => setAddress(e.target.value)}
              className={combineClasses(
                glassmorphism,
                'w-full placeholder:text-gray-500'
              )}
              required
            />
          </div>
          <div>
            <label
              htmlFor="officeLat"
              className="block text-gray-700 text-sm font-medium mb-1"
            >
              Latitude
            </label>
            <Input
              id="officeLat"
              type="number"
              step="any"
              value={lat}
              onChange={e => setLat(e.target.value)}
              className={combineClasses(
                glassmorphism,
                'w-full placeholder:text-gray-500'
              )}
              required
            />
          </div>
          <div>
            <label
              htmlFor="officeLng"
              className="block text-gray-700 text-sm font-medium mb-1"
            >
              Longitude
            </label>
            <Input
              id="officeLng"
              type="number"
              step="any"
              value={lng}
              onChange={e => setLng(e.target.value)}
              className={combineClasses(
                glassmorphism,
                'w-full placeholder:text-gray-500'
              )}
              required
            />
          </div>
          <div>
            <label
              htmlFor="officeNumGuards"
              className="block text-gray-700 text-sm font-medium mb-1"
            >
              Number of Guards
            </label>
            <Input
              id="officeNumGuards"
              type="number"
              value={numGuards}
              onChange={e => setNumGuards(e.target.value)}
              className={combineClasses(
                glassmorphism,
                'w-full placeholder:text-gray-500'
              )}
              required
            />
          </div>
          <div>
            <label
              htmlFor="officeSupervisors"
              className="block text-gray-700 text-sm font-medium mb-1"
            >
              Supervisors
            </label>
            <Input
              id="officeSupervisors"
              type="number"
              value={supervisors}
              onChange={e => setSupervisors(e.target.value)}
              className={combineClasses(
                glassmorphism,
                'w-full placeholder:text-gray-500'
              )}
              required
            />
          </div>
          <div>
            <label
              htmlFor="officeEmployees"
              className="block text-gray-700 text-sm font-medium mb-1"
            >
              Employees
            </label>
            <Input
              id="officeEmployees"
              type="number"
              value={employeesCount}
              onChange={e => setEmployeesCount(e.target.value)}
              className={combineClasses(
                glassmorphism,
                'w-full placeholder:text-gray-500'
              )}
              required
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              onClick={onClose}
              className="bg-gray-500/20 text-gray-700 hover:bg-gray-500/30"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-green-500/20 text-green-600 hover:bg-green-500/30"
            >
              Save
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Modal for adding/editing employees
const EmployeeModal = ({ isOpen, onClose, employee, onSave }) => {
  const [name, setName] = useState(employee ? employee.name : '');
  const [role, setRole] = useState(employee ? employee.role : '');
  const [image, setImage] = useState(
    employee ? employee.image : 'https://randomuser.me/api/portraits/men/99.jpg'
  ); // Default image
  const [officeId, setOfficeId] = useState(employee ? employee.officeId : ''); // Added officeId
  const [status, setStatus] = useState(employee ? employee.status : 'Off Duty'); // Added status

  const handleSubmit = e => {
    e.preventDefault();
    onSave({
      ...employee,
      name,
      role,
      image,
      officeId,
      status,
      id: employee ? employee.id : Date.now().toString(),
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        className={combineClasses(
          glassmorphism,
          'p-6 w-full max-w-md space-y-4'
        )}
      >
        <h2 className="text-2xl font-bold text-green-400">
          {employee ? 'Edit Employee' : 'Add New Employee'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="employeeName"
              className="block text-gray-700 text-sm font-medium mb-1"
            >
              Name
            </label>
            <Input
              id="employeeName"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className={combineClasses(
                glassmorphism,
                'w-full placeholder:text-gray-500'
              )}
              required
            />
          </div>
          <div>
            <label
              htmlFor="employeeRole"
              className="block text-gray-700 text-sm font-medium mb-1"
            >
              Role
            </label>
            <Input
              id="employeeRole"
              type="text"
              value={role}
              onChange={e => setRole(e.target.value)}
              className={combineClasses(
                glassmorphism,
                'w-full placeholder:text-gray-500'
              )}
              required
            />
          </div>
          <div>
            <label
              htmlFor="employeeImage"
              className="block text-gray-700 text-sm font-medium mb-1"
            >
              Image URL (Optional)
            </label>
            <Input
              id="employeeImage"
              type="text"
              value={image}
              onChange={e => setImage(e.target.value)}
              className={combineClasses(
                glassmorphism,
                'w-full placeholder:text-gray-500'
              )}
            />
          </div>
          {/* New fields for officeId and status */}
          <div>
            <label
              htmlFor="employeeOfficeId"
              className="block text-gray-700 text-sm font-medium mb-1"
            >
              Assigned Office ID
            </label>
            <Input
              id="employeeOfficeId"
              type="text"
              value={officeId}
              onChange={e => setOfficeId(e.target.value)}
              className={combineClasses(
                glassmorphism,
                'w-full placeholder:text-gray-500'
              )}
              required
            />
          </div>
          <div>
            <label
              htmlFor="employeeStatus"
              className="block text-gray-700 text-sm font-medium mb-1"
            >
              Status
            </label>
            <select
              id="employeeStatus"
              value={status}
              onChange={e => setStatus(e.target.value)}
              className={combineClasses(
                glassmorphism,
                'w-full bg-blue-100/10 p-2 rounded-md text-gray-900'
              )} // Text inside select is dark
              required
            >
              <option value="On Duty">On Duty</option>
              <option value="Off Duty">Off Duty</option>
            </select>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              onClick={onClose}
              className="bg-gray-500/20 text-gray-700 hover:bg-gray-500/30"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-green-500/20 text-green-600 hover:bg-green-500/30"
            >
              Save
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Modal for adding/editing absent shifts
const AbsentShiftModal = ({ isOpen, onClose, shift, onSave }) => {
  const [date, setDate] = useState(shift ? shift.date : '');
  const [time, setTime] = useState(shift ? shift.time : '');
  const [site, setSite] = useState(shift ? shift.site : '');

  const handleSubmit = e => {
    e.preventDefault();
    onSave({
      ...shift,
      date,
      time,
      site,
      id: shift ? shift.id : Date.now().toString(),
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        className={combineClasses(
          glassmorphism,
          'p-6 w-full max-w-md space-y-4'
        )}
      >
        <h2 className="text-2xl font-bold text-green-400">
          {shift ? 'Edit Absent Shift' : 'Add New Absent Shift'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="shiftDate"
              className="block text-gray-700 text-sm font-medium mb-1"
            >
              Date
            </label>
            <Input
              id="shiftDate"
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className={combineClasses(
                glassmorphism,
                'w-full placeholder:text-gray-500'
              )}
              required
            />
          </div>
          <div>
            <label
              htmlFor="shiftTime"
              className="block text-gray-700 text-sm font-medium mb-1"
            >
              Time (e.g., 4:00 PM - 12:00 AM)
            </label>
            <Input
              id="shiftTime"
              type="text"
              value={time}
              onChange={e => setTime(e.target.value)}
              className={combineClasses(
                glassmorphism,
                'w-full placeholder:text-gray-500'
              )}
              required
            />
          </div>
          <div>
            <label
              htmlFor="shiftSite"
              className="block text-gray-700 text-sm font-medium mb-1"
            >
              Site
            </label>
            <Input
              id="shiftSite"
              type="text"
              value={site}
              onChange={e => setSite(e.target.value)}
              className={combineClasses(
                glassmorphism,
                'w-full placeholder:text-gray-500'
              )}
              required
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              onClick={onClose}
              className="bg-gray-500/20 text-gray-700 hover:bg-gray-500/30"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-green-500/20 text-green-600 hover:bg-green-500/30"
            >
              Save
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// EmployeeCard, AbsentShiftCard, LiveRideCard remain as separate components
const EmployeeCard = ({ employee, onEdit, onDelete }) => (
  <motion.div
    variants={cardVariants}
    initial="hidden"
    animate="visible"
    className={combineClasses(glassmorphism, 'p-3 flex items-center gap-4')}
  >
    <img
      src={employee.image}
      alt={employee.name}
      className="w-10 h-10 rounded-full"
    />
    <div>
      <Typography variant="body1" className="text-gray-900 font-medium">
        {employee.name}
      </Typography>
      <Typography variant="body2" className="text-gray-700">
        {employee.role}
      </Typography>
    </div>
  </motion.div>
);

const AbsentShiftCard = ({ shift, onEdit, onDelete }) => (
  <motion.div
    variants={cardVariants}
    initial="hidden"
    animate="visible"
    className={combineClasses(glassmorphism, 'p-4 mb-4')}
  >
    <div className="flex justify-between items-center mb-2">
      <div>
        <Typography variant="body1" className="text-gray-900">
          <span className="font-medium">Date:</span> {shift.date}
        </Typography>
        <Typography variant="body1" className="text-gray-900">
          <span className="font-medium">Time:</span> {shift.time}
        </Typography>
        <Typography variant="body1" className="text-gray-900">
          <span className="font-medium">Site:</span> {shift.site}
        </Typography>
      </div>
      <div className="flex gap-2">
        <Button
          onClick={() => onEdit(shift)}
          size="small"
          className="bg-blue-500/20 text-blue-600 hover:bg-blue-500/30 p-1 rounded-full"
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Button
          onClick={() => onDelete(shift.id)}
          size="small"
          className="bg-red-500/20 text-red-600 hover:bg-red-500/30 p-1 rounded-full"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  </motion.div>
);

// Main Dashboard Component
const Dashboard = ({ isAuthenticated }) => {
  // Log screen view
  useScreenLogging('Dashboard', {
    component: 'Dashboard',
    isAuthenticated,
  });

  // State for all modifiable data
  const [assignedUsersCount, setAssignedUsersCount] = useState('0/0'); // Format: valid/total non-admin
  const [totalSitesCount, setTotalSitesCount] = useState(0);

  // Main data states, initialized to empty arrays as they will be populated by API
  const [employees, setEmployees] = useState([]); // All employees fetched from API
  const [siteEmployees, setSiteEmployees] = useState([]); // Employees assigned to the currently selected site
  const [absentShifts, setAbsentShifts] = useState(mockAbsentShifts); // Mocked, no API provided
  const [liveRides, setLiveRides] = useState(mockLiveRides); // Mocked, no API provided
  const [officeLocations, setOfficeLocations] = useState([]); // All sites fetched from API
  const [guardLocations, setGuardLocations] = useState([]); // All guards with their locations for the map

  // New states for filtered data to be displayed on the map
  const [displayedOfficeLocations, setDisplayedOfficeLocations] = useState([]);
  const [displayedGuardLocations, setDisplayedGuardLocations] = useState([]);

  // State for modals
  const [isOfficeModalOpen, setIsOfficeModalOpen] = useState(false);
  const [currentOffice, setCurrentOffice] = useState(null);
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [isAbsentShiftModalOpen, setIsAbsentShiftModalOpen] = useState(false);
  const [currentAbsentShift, setCurrentAbsentShift] = useState(null);
  // Removed isStatsModalOpen state
  // Removed isTestApiModalOpen state

  const [selectedSite, setSelectedSite] = useState(null); // Currently selected site from map click or search
  const [selectedShiftId, setSelectedShiftId] = useState(null); // Currently selected shift ID
  // mapCenter and mapZoom are now primarily controlled by MapComponent's internal state
  // and updated via imperative handle for specific focus actions.
  const [mapCenter, setMapCenter] = useState({ lat: 20.5937, lng: 78.9629 }); // Default center for India
  const [mapZoom, setMapZoom] = useState(5); // Default zoom level

  // States for top bar functionality
  const [isTrackRidesPanelOpen, setIsTrackRidesPanelOpen] = useState(false);
  const [isAddSiteModalOpen, setIsAddSiteModalOpen] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const profileDropdownRef = useRef(null);

  // Search state for overlay search bar
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showSiteDropdown, setShowSiteDropdown] = useState(false);
  const searchInputRef = useRef(null);

  // Reverted map focus logic: hasSearchedAndFocused state is maintained as per user's provided file
  const [hasSearchedAndFocused, setHasSearchedAndFocused] = useState(false);
  
  // Ref to track if initial data load is complete (to avoid clearing geofences on first load)
  const initialDataLoadedRef = useRef(false);

  // State for showing the employee table (full screen)
  const [showEmployeeInfoTable, setShowEmployeeInfoTable] = useState(false);
  // State to determine which employee list to show in EmployeeInfoTable
  const [employeeTableData, setEmployeeTableData] = useState([]);

  // State for showing the Site Schedule modal
  const [isSiteScheduleOpenInPanel, setIsSiteScheduleOpenInPanel] =
    useState(false);

  // State for managing the currently selected ride for map display
  const [selectedRideForMap, setSelectedRideForMap] = useState(null);

  // State for Site Information Card visibility
  const [isSiteInfoCardOpen, setIsSiteInfoCardOpen] = useState(false);

  // Loading and Error states for API calls
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for current date and time
  const [currentDateTime, setCurrentDateTime] = useState('');

  const navigate = useNavigate();
  const mapComponentRef = useRef(); // Ref for the MapComponent

  // Callback for fetching search suggestions
  const fetchNominatimSuggestions = useCallback(async query => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query
        )}&countrycodes=in&limit=5`
      );
      const data = await response.json();
      const formattedSuggestions = data.map(item => ({
        display_name: item.display_name,
        lat: parseFloat(item.lat),
        lon: parseFloat(item.lon),
      }));
      setSuggestions(formattedSuggestions);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    }
  }, []);

  // Callback for handling site selection from dropdown
  const handleSiteSelection = useCallback(
    site => {
      setSearchQuery(site.name);
      setShowSiteDropdown(false);
      setShowSuggestions(false);
      setSuggestions([]);

      // Set the selected site and show sidebar
      setSelectedSite(site);
      setSelectedShiftId(null); // Reset shift selection when selecting a new site
      setIsSiteInfoCardOpen(true);
      
      // Pan to site location on map
      if (mapComponentRef.current) {
        mapComponentRef.current.focusOnSiteGeofence(site);
        
        // Show geofence for the selected site (without clearing others)
        if (site.geofenceDetails) {
          mapComponentRef.current.showSiteGeofence(site);
        }
      }
      
      // Keep all sites visible, but filter employees to show only those at the selected site
      // Don't filter displayedOfficeLocations - keep all sites visible
      setDisplayedGuardLocations(
        employees.filter(
          emp =>
            emp.officeId === site.id &&
            emp.lat != null &&
            emp.lng != null &&
            emp.status === 'On Duty'
        )
      );
    },
    [employees]
  );

  // Callback for handling suggestion selection (for external locations)
  const handleSelectSuggestion = useCallback(
    suggestion => {
      setSearchQuery(suggestion.display_name);
      setShowSuggestions(false);
      setShowSiteDropdown(false);
      setSuggestions([]);

      // Find if this suggestion matches any existing site
      const matchingSite = officeLocations.find(
        site =>
          site.name
            .toLowerCase()
            .includes(suggestion.display_name.toLowerCase()) ||
          site.address
            .toLowerCase()
            .includes(suggestion.display_name.toLowerCase())
      );

      if (matchingSite) {
        setSelectedSite(matchingSite);
        setSelectedShiftId(null); // Reset shift selection when selecting a new site
        setIsSiteInfoCardOpen(true);
        if (mapComponentRef.current) {
          mapComponentRef.current.focusOnSiteGeofence(matchingSite);
          
          // Show geofence for the matching site (without clearing others)
          if (matchingSite.geofenceDetails) {
            mapComponentRef.current.showSiteGeofence(matchingSite);
          }
        }
        
        // Filter employees to show only those at the matching site
        setDisplayedGuardLocations(
          employees.filter(
            emp =>
              emp.officeId === matchingSite.id &&
              emp.lat != null &&
              emp.lng != null &&
              emp.status === 'On Duty'
          )
        );
      } else {
        // If no matching site, pan to the suggestion location
        if (mapComponentRef.current) {
          mapComponentRef.current.panToAndZoom(
            {
              lat: suggestion.lat,
              lng: suggestion.lon,
            },
            12
          );
        }
        
        // Show all employees when navigating to an external location
        setDisplayedGuardLocations(
          employees.filter(
            emp => emp.lat != null && emp.lng != null && emp.status === 'On Duty'
          )
        );
      }
    },
    [officeLocations, employees]
  );

  // Callback for handling search input blur
  const handleSearchInputBlur = useCallback(() => {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      setShowSuggestions(false);
      setShowSiteDropdown(false);
    }, 200);
  }, []);

  // Callback for handling search input focus
  const handleSearchInputFocus = useCallback(() => {
    // Show site dropdown when focusing on search input
    setShowSiteDropdown(true);
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  }, [suggestions.length]);

  // Callback for handling search input click
  const handleSearchInputClick = useCallback(() => {
    // Show site dropdown when clicking on search input
    setShowSiteDropdown(true);
  }, []);

  // Callback for handling search input change
  const handleSearchInputChange = useCallback((e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Show site dropdown when typing
    if (value.length > 0) {
      setShowSiteDropdown(true);
    }
  }, []);

  // Callback for handling Enter key press
  const handleSearchKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      // Try to find exact match first
      const lowerCaseQuery = searchQuery.toLowerCase();
      const foundSite = officeLocations.find(
        site =>
          site.name.toLowerCase() === lowerCaseQuery ||
          site.address.toLowerCase().includes(lowerCaseQuery)
      );

      if (foundSite) {
        handleSiteSelection(foundSite);
      } else {
        // If no exact match, close dropdown and let external suggestions show
        setShowSiteDropdown(false);
      }
    }
  }, [searchQuery, officeLocations, handleSiteSelection]);

  // Callback for getting selected ride route
  const getSelectedRideRoute = useCallback(() => {
    if (!selectedRideForMap) return null;
    return liveRides.find(ride => ride.id === selectedRideForMap);
  }, [selectedRideForMap, liveRides]);

  // Callback for showing employee info table
  const handleShowEmployeeInfoTable = useCallback(async employeeList => {
    // Get current date in YYYY-MM-DD format
    const getCurrentDate = () => {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    try {
      // Get the current date and site
      const currentDate = getCurrentDate();
      const siteId = selectedSite?.id;

      // If we have a site, try to fetch notes from getShiftRoster
      if (siteId && employeeList && employeeList.length > 0) {
        try {
          const shiftData = await getShiftRoster(siteId, currentDate);
          console.log('🔍 Fetched shift roster data:', shiftData);

          // Create a map of user notes from the shift roster
          const userNotesMap = {};
          
          if (Array.isArray(shiftData)) {
            shiftData.forEach(shift => {
              if (shift.user_datas && Array.isArray(shift.user_datas)) {
                shift.user_datas.forEach(userData => {
                  const userId = userData.user_id;
                  const notes = userData.user_notes || userData.notes || '';
                  if (userId && notes) {
                    userNotesMap[userId] = notes;
                  }
                });
              }
            });
          }

          // Update employee list with notes from shift roster
          const employeesWithNotes = employeeList.map(employee => {
            const userId = employee.id;
            const note = userNotesMap[userId] || employee.note || 'No Note';
            return {
              ...employee,
              note: note
            };
          });

          setEmployeeTableData(employeesWithNotes);
        } catch (error) {
          console.error('Error fetching notes from getShiftRoster:', error);
          // If fetching fails, use the original employee list
          setEmployeeTableData(employeeList);
        }
      } else {
        // No site selected, just use the original employee list
        setEmployeeTableData(employeeList);
      }
    } catch (error) {
      console.error('Error in handleShowEmployeeInfoTable:', error);
      setEmployeeTableData(employeeList);
    }
    
    setShowEmployeeInfoTable(true);
  }, [selectedSite]);

  // Callback for closing employee info table
  const handleCloseEmployeeInfoTable = useCallback(() => {
    setShowEmployeeInfoTable(false);
    setEmployeeTableData([]);
  }, []);

  // Callback for handling shift selection
  const handleShiftSelected = useCallback((shiftId) => {
    console.log('🔍 Dashboard: Shift selected:', shiftId);
    setSelectedShiftId(shiftId);
    
    // Filter employees for the map based on selected shift and duty status
    if (selectedSite && employees.length > 0) {
      // Get employees assigned to the selected site and shift
      const siteEmployees = employees.filter(emp => emp.officeId === selectedSite.id);
      
      // Filter by duty status (On Duty) and selected shift
      const filteredEmployees = siteEmployees.filter(emp => 
        emp.lat != null && 
        emp.lng != null && 
        emp.status === 'On Duty'
      );
      
      setDisplayedGuardLocations(filteredEmployees);
    }
  }, [selectedSite, employees]);

  // Callback for receiving employees with location data from SiteInformationCard
  const handleEmployeesForMap = useCallback((employeesWithLocations) => {
    console.log('🔍 Dashboard: Received employees with locations:', employeesWithLocations);
    if (employeesWithLocations && employeesWithLocations.length > 0) {
      // Update displayed guard locations with employees that have location data
      // Filter by duty status (On Duty) - shift filtering is handled by SiteInformationCard
      const employeesToDisplay = employeesWithLocations.filter(
        emp => emp.lat != null && emp.lng != null && emp.status === 'On Duty'
      );
      if (employeesToDisplay.length > 0) {
        setDisplayedGuardLocations(employeesToDisplay);
      }
    }
  }, []);

  // Callback for handling employee card clicks - pan and zoom to employee location on map
  const handleEmployeeClick = useCallback((employee) => {
    console.log('📍 Dashboard: Employee card clicked:', employee);
    
    // Check if employee has valid location data
    if (employee.lat && employee.lng && typeof employee.lat === 'number' && typeof employee.lng === 'number') {
      // Use MapComponent's panToAndZoom method via the ref
      if (mapComponentRef.current) {
        console.log(`📍 Panning to employee ${employee.name} at ${employee.lat}, ${employee.lng}`);
        mapComponentRef.current.panToAndZoom(
          { lat: employee.lat, lng: employee.lng },
          18 // Zoom level 18 for close-up view
        );
      } else {
        console.error('MapComponent ref is not available');
      }
    } else {
      console.warn('Employee does not have valid location data:', employee);
    }
  }, []);

  // Callback for refreshing employee locations
  const refreshEmployeeLocations = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const employeesData = await dashboardApi.getAllUsersWithDutyData();
      
      // Fetch profile images for all employees
      const employeesWithPhotos = await Promise.all(
        (employeesData.users || []).map(async (user) => {
          let imageUrl = employeeIcon; // Default to employee icon
          
          try {
            if (user.user_id) {
              imageUrl = await dashboardApi.getProfilePhoto(user.user_id);
            }
          } catch (photoError) {
            console.error(`Failed to get profile photo for ${user.user_name}:`, photoError);
          }
          
          return {
            id: user.user_id,
            name: user.user_name,
            role: user.spec_role || user.role_name, // Use spec_role instead of role_name
            image: imageUrl,
            officeId: user.site_id || 'N/A',
            status: user.on_duty ? 'On Duty' : 'Off Duty',
            note: 'No Note', // Placeholder until notes are fetched
            lat: user.last_location?.latitude,
            lng: user.last_location?.longitude,
          };
        })
      );
      
      setEmployees(employeesWithPhotos);
      const onDutyEmployeesWithLocation = employeesWithPhotos.filter(
        emp =>
          emp.lat != null &&
          emp.lng != null &&
          emp.status === 'On Duty'
      );
      setGuardLocations(onDutyEmployeesWithLocation);
      setDisplayedGuardLocations(onDutyEmployeesWithLocation);
    } catch (err) {
      console.error('Error refreshing employee locations:', err);
    }
  }, [isAuthenticated]);

  // Prevent API calls when not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      console.log('Dashboard: User not authenticated, skipping API calls');
      return;
    }

    // Only proceed with API calls if authenticated
    console.log('Dashboard: User authenticated, proceeding with API calls');
  }, [isAuthenticated]);

  // Removed duplicate fetchInitialData useEffect - using fetchInitialDashboardData instead

  // Effect for updating current date and time
  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      setCurrentDateTime(
        now.toLocaleString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
      );
    };

    updateDateTime();
    const interval = setInterval(updateDateTime, 1000);

    return () => clearInterval(interval);
  }, []);

  // Effect for handling site selection changes
  useEffect(() => {
    if (selectedSite) {
      const siteEmployees = employees.filter(
        emp => emp.officeId === selectedSite.id
      );
      setSiteEmployees(siteEmployees);
    } else {
      setSiteEmployees([]);
      setSelectedShiftId(null); // Reset selected shift when no site is selected
    }
  }, [selectedSite, employees]);

  // Effect for handling search query changes - REMOVED immediate search logic
  // Now search only happens when user selects from dropdown or presses Enter
  // This effect is kept for clearing search when query is empty
  useEffect(() => {
    if (!searchQuery) {
      // If search query is empty, show all sites and all guards.
      setSelectedSite(null);
      setDisplayedOfficeLocations(officeLocations);
      setDisplayedGuardLocations(guardLocations);
      setIsSiteInfoCardOpen(false);
      
      // Only clear geofences if user had previously searched (not on initial load)
      // Save the current value of hasSearchedAndFocused before resetting it
      if (hasSearchedAndFocused && initialDataLoadedRef.current && mapComponentRef.current) {
        mapComponentRef.current.clearGeofences();
      }
      
      setHasSearchedAndFocused(false);
    }
  }, [searchQuery, officeLocations, guardLocations, hasSearchedAndFocused]);

  // Effect for search suggestions timeout (only for external locations)
  useEffect(() => {
    if (searchQuery.length >= 3) {
      const handler = setTimeout(() => {
        fetchNominatimSuggestions(searchQuery);
      }, 300);
      return () => clearTimeout(handler);
    } else {
      setSuggestions([]);
    }
  }, [searchQuery, fetchNominatimSuggestions]);

  // Effect for handling click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchContainer = document.querySelector('.search-container');
      if (searchContainer && !searchContainer.contains(event.target)) {
        setShowSiteDropdown(false);
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Effect to reset displayed locations when all panels are closed and no search is active
  // This effect ensures the displayed markers revert to showing all sites/guards,
  // but it explicitly DOES NOT control the map's center/zoom.
  useEffect(() => {
    if (
      !isSiteInfoCardOpen &&
      !isTrackRidesPanelOpen &&
      !showEmployeeInfoTable &&
      !searchQuery
    ) {
      setDisplayedOfficeLocations(officeLocations);
      setDisplayedGuardLocations(guardLocations);
    }
  }, [
    isSiteInfoCardOpen,
    isTrackRidesPanelOpen,
    showEmployeeInfoTable,
    searchQuery,
    officeLocations,
    guardLocations,
  ]);

  // Effect to update current date and time
  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      const options = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      };
      setCurrentDateTime(now.toLocaleString('en-US', options));
    };

    updateDateTime();
    const intervalId = setInterval(updateDateTime, 1000);

    return () => clearInterval(intervalId);
  }, []);

  // Fetch initial data on component mount, and set up real-time updates
  useEffect(() => {
    const fetchInitialDashboardData = async () => {
      // Check authentication before making API calls
      if (!isAuthenticated) {
        console.log('Dashboard: Skipping API calls - user not authenticated');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const [
          allSitesResponse,
          allUsersResponse,
          validUsersCountResponse,
          totalNonAdminCountResponse,
        ] = await Promise.all([
          dashboardApi.getAllSites(),
          dashboardApi.getAllUsersWithDutyData(),
          dashboardApi.getValidUsersCount(),
          dashboardApi.getTotalNonAdminCount(),
        ]);

        // Ensure allSitesResponse is an array before mapping
        const sitesArray = Array.isArray(allSitesResponse)
          ? allSitesResponse
          : [];

        const fetchedOfficeLocations = sitesArray.map(site => {
          let lat = 0;
          let lng = 0;
          let geofenceDetails = null;

          try {
            const parsedDetails = JSON.parse(site.geofence_details);
            geofenceDetails = parsedDetails;

            if (
              parsedDetails.type === 'Polygon' &&
              parsedDetails.coordinates &&
              parsedDetails.coordinates.length > 0
            ) {
              // For polygons, use the first coordinate as the site's primary lat/lng for marker placement
              lat = parsedDetails.coordinates[0].latitude;
              lng = parsedDetails.coordinates[0].longitude;
            } else if (
              parsedDetails.type === 'Circle' &&
              parsedDetails.center
            ) {
              lat = parsedDetails.center.latitude;
              lng = parsedDetails.center.longitude;
            } else {
              console.warn(
                `Malformed or unsupported geofence_details type for site ${site.site_id}:`,
                site.geofence_details
              );
              // Fallback: Use default coordinates for India if geofence is malformed
              lat = 20.5937;
              lng = 78.9629;
            }
          } catch (parseError) {
            console.error(
              `Error parsing geofence_details for site ${site.site_id}:`,
              parseError,
              site.geofence_details
            );
            // Fallback: Use default coordinates for India if parsing fails
            lat = 20.5937; // Default to center of India
            lng = 78.9629;
          }

          const result = {
            id: site.site_id,
            name: site.site_name,
            address: site.address,
            lat: lat,
            lng: lng,
            geofenceDetails: geofenceDetails,
            numGuards: 0,
            supervisors: 0,
            employees: 0, // These might need to be fetched separately or derived
            radius: geofenceDetails?.radius || 500, // Default radius if not specified in geofenceDetails
          };

          if (lat === 0 && lng === 0) {
            console.warn(
              `Site ${site.site_name} has invalid coordinates: lat=${lat}, lng=${lng}`
            );
          }

          return result;
        });
        
        // Sort sites alphabetically by name
        const sortedOfficeLocations = [...fetchedOfficeLocations].sort((a, b) => {
          const nameA = (a?.name || '').toLowerCase();
          const nameB = (b?.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        setOfficeLocations(sortedOfficeLocations);
        setDisplayedOfficeLocations(sortedOfficeLocations); // Initially display all sites
        
        // Mark that initial data load is complete
        initialDataLoadedRef.current = true;

        // Ensure allUsersResponse and allUsersResponse.users exist before mapping
        const allUsersArray =
          allUsersResponse && allUsersResponse.users
            ? allUsersResponse.users
            : [];
        const fetchedEmployees = await Promise.all(
          allUsersArray.map(async user => {
            let imageUrl = employeeIcon; // Default to employee icon
            
            // Try to fetch profile photo
            try {
              if (user.user_id) {
                imageUrl = await dashboardApi.getProfilePhoto(user.user_id);
              }
            } catch (photoError) {
              console.error(
                `Failed to get profile photo for ${user.user_name}:`,
                photoError
              );
              // Fallback: try image_path if available
              if (user.image_path) {
                try {
                  imageUrl = await dashboardApi.getPresignedS3Url(user.image_path);
                  if (!imageUrl) {
                    imageUrl = employeeIcon; // Use default employee icon
                  }
                } catch (imgError) {
                  console.error(
                    `Failed to get presigned URL for ${user.user_name}:`,
                    imgError
                  );
                  imageUrl = employeeIcon; // Use default employee icon
                }
              }
            }
            
            return {
              id: user.user_id,
              name: user.user_name,
              role: user.spec_role || user.role_name, // Use spec_role instead of role_name
              image: imageUrl,
              officeId: user.site_id || 'N/A',
              status: user.on_duty ? 'On Duty' : 'Off Duty',
              note: 'No Note', // Placeholder until notes are fetched
              lat: user.last_location?.latitude, // Include initial location if available
              lng: user.last_location?.longitude, // Include initial location if available
            };
          })
        );
        setEmployees(fetchedEmployees);
        const initialGuardLocations = fetchedEmployees.filter(
          emp => emp.lat != null && emp.lng != null && emp.status === 'On Duty'
        );
        setGuardLocations(initialGuardLocations);
        setDisplayedGuardLocations(initialGuardLocations); // Initially display all employees with location data

        setAssignedUsersCount(
          `${validUsersCountResponse.total_scheduled || 0}/${totalNonAdminCountResponse.count || 0}`
        );
        setTotalSitesCount(sitesArray.length);
      } catch (err) {
        console.error('Error fetching initial dashboard data:', err);
        setError(err.message || 'Failed to load initial dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchInitialDashboardData();
      // Location refresh removed - updates only on initial load
    } else {
      // Fallback for unauthenticated state or if isAuthenticated becomes false
      setLoading(false);
      setError(null);
      setOfficeLocations([]);
      setEmployees([]);
      setLiveRides(mockLiveRides);
      setAbsentShifts(mockAbsentShifts);
      setAssignedUsersCount('N/A');
      setTotalSitesCount(0);
      setGuardLocations([]);
      setDisplayedOfficeLocations([]);
      setDisplayedGuardLocations([]);
    }
  }, [isAuthenticated]);

  // Callback for handling marker clicks
  const handleMarkerClick = useCallback(site => {
    setSelectedSite(site);
    setSelectedShiftId(null); // Reset shift selection when clicking a marker
    setIsSiteInfoCardOpen(true);
    setIsTrackRidesPanelOpen(false);
    setSelectedRideForMap(null);
    setShowEmployeeInfoTable(false);
    setIsSiteScheduleOpenInPanel(false);
    
    // Show geofence for the selected site (without clearing others)
    if (mapComponentRef.current) {
      mapComponentRef.current.focusOnSiteGeofence(site);
      if (site.geofenceDetails) {
        mapComponentRef.current.showSiteGeofence(site);
      }
    }
    
    // Filter employees to show only those at the selected site
    setDisplayedGuardLocations(
      employees.filter(
        emp =>
          emp.officeId === site.id &&
          emp.lat != null &&
          emp.lng != null &&
          emp.status === 'On Duty'
      )
    );
  }, [employees]);

  // Add authentication check after all hooks are declared
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            Authentication Required
          </h2>
          <p className="text-gray-700 mb-4">
            You need to be logged in to access the dashboard.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Please log in with your credentials to continue.
          </p>
          <button
            onClick={() => (window.location.href = '/')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Effect to update selectedSite and trigger map focus based on search query

  // Logout logic (can be here or centralized)
  const handleLogout = () => {
    console.log('User logged out from Dashboard');
    // Implement actual logout logic here
  };

  // Handle navigation to Profile.js using useNavigate
  const handleProfileClick = () => {
    navigate('/profile'); // Navigate to the Profile page using react-router-dom
    setShowProfileDropdown(false); // Close dropdown after navigation
  };

  // Handlers for CRUD operations (these will eventually interact with APIs)
  // NOTE: These are client-side updates. In a real app, these would call backend APIs.
  const handleSaveOffice = office => {
    let updatedLocations;
    if (office.id) {
      updatedLocations = officeLocations.map(o => (o.id === office.id ? office : o));
    } else {
      updatedLocations = [
        ...officeLocations,
        { ...office, id: Date.now().toString() },
      ];
    }
    
    // Sort sites alphabetically by name
    const sortedLocations = [...updatedLocations].sort((a, b) => {
      const nameA = (a?.name || '').toLowerCase();
      const nameB = (b?.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    setOfficeLocations(sortedLocations);
    setDisplayedOfficeLocations(sortedLocations);
  };

  const handleDeleteOffice = officeId => {
    setOfficeLocations(officeLocations.filter(o => o.id !== officeId));
  };

  const handleSaveEmployee = employee => {
    if (employee.id) {
      setEmployees(employees.map(e => (e.id === employee.id ? employee : e)));
      // Update guard location if the employee is a guard
      if (employee.role === 'Guard') {
        setGuardLocations(prevLocations =>
          prevLocations.map(g =>
            g.name === employee.name
              ? {
                  ...g,
                  image: employee.image,
                  officeId: employee.officeId,
                }
              : g
          )
        );
      }
    } else {
      const newEmployee = { ...employee, id: Date.now().toString() };
      setEmployees([...employees, newEmployee]);
      // Add to guard locations if it's a guard
      if (newEmployee.role === 'Guard') {
        setGuardLocations(prevLocations => [
          ...prevLocations,
          {
            id: `guard${prevLocations.length + 1}`,
            name: newEmployee.name,
            lat: mapCenter.lat, // Default lat/lng for new guards, consider a more robust solution
            lng: mapCenter.lng,
            officeId: newEmployee.officeId,
            image: newEmployee.image,
          },
        ]);
      }
    }
  };

  const handleDeleteEmployee = employeeId => {
    setEmployees(employees.filter(e => e.id !== employeeId));
    setGuardLocations(
      guardLocations.filter(
        g => g.name !== employees.find(emp => emp.id === employeeId)?.name
      )
    );
  };

  const handleSaveAbsentShift = shift => {
    if (shift.id) {
      setAbsentShifts(absentShifts.map(s => (s.id === shift.id ? shift : s)));
    } else {
      setAbsentShifts([
        ...absentShifts,
        { ...shift, id: Date.now().toString() },
      ]);
    }
  };

  const handleDeleteAbsentShift = shiftId => {
    setAbsentShifts(absentShifts.filter(s => s.id !== shiftId));
  };

  const handleFocusMap = site => {
    // This function will be passed to SiteInformationCard
    // It now calls the imperative handle method to pan and zoom
    console.log(
      `Focusing map on site: ${site.name} at Lat: ${site.lat}, Lng: ${site.lng}`
    );
    if (mapComponentRef.current) {
      mapComponentRef.current.focusOnSiteGeofence(site);
      
      // Show geofence for the focused site
      if (site.geofenceDetails) {
        mapComponentRef.current.showSiteGeofence(site);
      }
      
      setHasSearchedAndFocused(true); // Set flag to true after focusing
    }
  };

  // Handler for dispatching new rides
  const handleDispatchNewRide = ride => {
    // This is a placeholder function for handling new ride dispatch
    // In a real application, this would make an API call to dispatch the ride
    console.log('Dispatching new ride:', ride);
    // You can implement the actual logic here
  };

  // Placeholder for officer insight data - not API integrated as no API provided
  const officerInsights = [
    {
      id: 'insight1',
      type: 'activity',
      description: 'Increased activity detected near site A. Investigate.',
      severity: 'high',
    },
    {
      id: 'insight2',
      type: 'alert',
      description: 'Guard 123 moved outside geofence at Office 1.',
      severity: 'critical',
    },
    {
      id: 'insight3',
      type: 'system',
      description: 'Battery low on vehicle XYZ.',
      severity: 'medium',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-700">
        <svg
          className="animate-spin -ml-1 mr-3 h-8 w-8 text-green-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        Loading dashboard data...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-red-600">
        Error: {error}
      </div>
    );
  }

  return (
    <div
      className="relative w-full h-full flex flex-col"
      style={{
        borderRadius: '12px',
        // border: '1px solid #FFF',
        // background: '#E6E6FA',
        // boxShadow:
        //   '0px 5px 20.1px 0px rgba(255,255,255,0.10), 0px 15px 30px 0px rgba(0,0,0,0.10), 0px 4px 25px 0px rgba(255,255,255,0.35) inset',
        // backdropFilter: 'blur(30px)',
      }}
    >
      {/* Dashboard Header - Breadcrumb Navigation and Track Live Rides Button */}
      {/* <div className="self-stretch h-12 px-6 py-3 bg-neutral-50/40 rounded-xl shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.35)] backdrop-blur-[30px] flex justify-between items-center gap-2.5 mb-4 flex-shrink-0"> */}
        {/* Breadcrumb Navigation */}
        {/* <div className="flex items-center gap-2.5"> */}
          {/* <div className="justify-center text-black text-lg font-normal font-['Open_Sans'] leading-relaxed">
            Dashboard
          </div> */}
          {/* Using Lucide ArrowRight */}
          {/* <div className="w-6 h-6 relative overflow-hidden">
            <ArrowRight className="w-4 h-4 left-[8px] top-[5px] absolute bg-black" />{' '}
          </div> */}
          {/* <div className="justify-center text-black text-lg font-normal font-['Open_Sans'] leading-relaxed">
            Site Info
          </div> */}
          {/* {selectedSite &&
            isSiteScheduleOpenInPanel && ( // Only show if Site Schedule is open */}
              <>{/* Using Lucide ArrowRight */}
                {/* <div className="w-6 h-6 relative overflow-hidden">
                  <ArrowRight className="w-4 h-4 left-[8px] top-[5px] absolute bg-black" />{' '}
                </div> */}
                {/* <div className="justify-center text-black text-lg font-normal font-['Open_Sans'] leading-relaxed">
                  Site Schedule
                </div> */}
              </>
            {/* )} */}
        {/* </div> */}

        {/* Right-side buttons in header */}
        {/* <div className="flex items-center gap-4"> */}
          {/* Display Current Date and Time */}
          {/* <div className="text-black text-sm font-normal font-['Open_Sans'] leading-relaxed mr-4">
            {currentDateTime}
          </div> */}

          {/* Removed the "Track live rides" button entirely from the header */}
        {/* </div> */}
      {/* </div> */}

      {/* Authentication Status Banner */}
      {/* {!isAuthenticated && (
        <div className="mx-4 mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Authentication Required
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  You need to be logged in to access dashboard data. Please log
                  in to continue.
                </p>
              </div>
            </div>
          </div>
        </div>
      )} */}

      {/* hide */}

      {/* Main Content Area - This needs to be a flex row */}
      <div className="flex flex-1 w-full overflow-hidden gap-4">
        {/* Main Dashboard Area (Map or Fullscreen Employee Table) */}
        <div
          className={combineClasses(
            'flex-1 flex flex-col h-full',
            (selectedSite && !isSiteScheduleOpenInPanel) ||
              isTrackRidesPanelOpen
              ? ''
              : ''
          )}
        >
          {/* Dashboard Card (Map) */}
          <div className="dashboard-card map-card relative w-full h-full rounded-xl overflow-hidden shadow-xl flex-shrink-0">
            {/* Search Bar Overlay - Placed directly on the map */}
            <div className="absolute top-4 left-4 z-10 search-container">
              <div
                data-property-1="Normal"
                className="w-96 h-14 pl-4 pr-3.5 py-2.5 bg-neutral-50/80 rounded-[10px] shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-black backdrop-blur-[30px] flex flex-col justify-center items-start gap-2.5"
              >
                <div className="inline-flex justify-start items-center gap-4 w-full">
                  <Search className="w-6 h-6 text-black" />
                  <input
                    type="text"
                    placeholder="Search Site"
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    onFocus={handleSearchInputFocus}
                    onBlur={handleSearchInputBlur}
                    onClick={handleSearchInputClick}
                    onKeyPress={handleSearchKeyPress}
                    className="flex-1 bg-transparent text-neutral-800 text-lg font-['Figtree'] leading-snug outline-none placeholder-neutral-400"
                  />
                </div>
              </div>
              
              {/* Site Dropdown - Similar to ScheduleAShift */}
              {showSiteDropdown && officeLocations.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                  <div className="p-2 border-b border-gray-200 bg-gray-50">
                    <p className="text-sm font-medium text-gray-700">
                      {searchQuery ? `Filtered results (${officeLocations.filter(site => 
                        site.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        site.address.toLowerCase().includes(searchQuery.toLowerCase())
                      ).length})` : `All Sites (${officeLocations.length})`}
                    </p>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {officeLocations
                      .filter(site => 
                        !searchQuery || 
                        site.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        site.address.toLowerCase().includes(searchQuery.toLowerCase())
                      )
                      .sort((a, b) => {
                        const nameA = (a?.name || '').toLowerCase();
                        const nameB = (b?.name || '').toLowerCase();
                        return nameA.localeCompare(nameB);
                      })
                      .map((site, index) => (
                        <div
                          key={site.id}
                          className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          onClick={() => handleSiteSelection(site)}
                        >
                          <div className="font-medium text-gray-900">{site.name}</div>
                          <div className="text-sm text-gray-500 truncate">
                            {site.address}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* External Location Suggestions */}
              {showSuggestions && suggestions.length > 0 && (
                <ul className="absolute top-full left-0 w-full bg-white/90 backdrop-blur-md rounded-md shadow-lg mt-2 z-20 overflow-hidden border border-gray-200">
                  <div className="p-2 border-b border-gray-200 bg-gray-50">
                    <p className="text-sm font-medium text-gray-700">
                      External Locations
                    </p>
                  </div>
                  {suggestions.map(suggestion => (
                    <li
                      key={suggestion.osm_id || suggestion.display_name}
                      onMouseDown={() => handleSelectSuggestion(suggestion)}
                      className="p-3 cursor-pointer hover:bg-gray-100 text-gray-800 border-b border-gray-100 last:border-b-0"
                    >
                      {suggestion.display_name}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <MapComponent
              ref={mapComponentRef} // Attach the ref to MapComponent
              officeLocations={displayedOfficeLocations}
              guardLocations={displayedGuardLocations}
              onMarkerClick={handleMarkerClick}
              mapCenter={mapCenter} // Initial map center (used only once by MapComponent internally)
              mapZoom={mapZoom} // Initial map zoom (used only once by MapComponent internally)
              activeRoutes={getSelectedRideRoute()}
              selectedSiteId={selectedSite?.id ?? null}
            />

            {/* Current Stats Overlay on the map - Positioned top-right */}
            <div className="absolute top-4 right-12 z-10">
              {/* Render CurrentStatsCard, which now manages its own expanded state */}
              <CurrentStatsCard
                assignedCount={assignedUsersCount}
                totalSitesCount={totalSitesCount}
              />
            </div>

            {/* Toggle button for Employee Table - remains at bottom-center */}
            {/* <button
              onClick={() => handleShowEmployeeInfoTable(employees)}
              className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10 bg-white/80 backdrop-blur-md text-gray-800 px-4 py-2 rounded-full shadow-lg hover:bg-white transition-colors flex items-center"
            >
              {showEmployeeInfoTable ? (
                <>
                  <MapPin className="w-5 h-5 mr-2" /> View Map
                </>
              ) : (
                <>
                  <Users className="w-5 h-5 mr-2" /> View All Employees
                </>
              )}
            </button> */}
          </div>

          {/* Employee Table Section (conditionally rendered) */}
          <AnimatePresence>
            {showEmployeeInfoTable && (
              <motion.div
                className="fixed inset-0 z-[60] flex items-center justify-center p-1 md:p-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <EmployeeInfoTable
                  employees={employeeTableData} // Use employeeTableData here
                  onClose={handleCloseEmployeeInfoTable}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right-side Panel for Site Info, Site Schedule, or Track Rides */}
        <AnimatePresence>
          {selectedSite &&
            isSiteInfoCardOpen && ( // Only render if a site is selected AND card is explicitly open
              <motion.div
                key="site-panel"
                initial={{ opacity: 0, x: '100%' }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: '100%' }}
                transition={{ duration: 0.3 }}
                className="w-96 flex-shrink-0 overflow-y-auto custom-scrollbar shadow-lg"
                style={{ ...rightPanelStyle, height: '100%' }}
              >
                {isSiteScheduleOpenInPanel ? (
                  <SiteScheduleComponent
                    site={selectedSite}
                    employees={employees}
                    onClose={() => setIsSiteScheduleOpenInPanel(false)}
                  />
                ) : (
                  <SiteInformationCard
                    site={selectedSite}
                    onEditSite={() => {
                      setCurrentOffice(selectedSite);
                      setIsOfficeModalOpen(true);
                    }}
                    onFocusMap={handleFocusMap}
                    siteEmployees={siteEmployees} // Pass API-fetched site employees
                    setIsSiteScheduleInPanel={setIsSiteScheduleOpenInPanel}
                    onShowAllEmployees={(employees) =>
                      handleShowEmployeeInfoTable(employees || siteEmployees)
                    } // Pass site-specific or shift-filtered employees
                    onClose={() => {
                      // Close the site info sidebar
                      setIsSiteInfoCardOpen(false);
                      
                      // Reset search query and selected site
                      setSearchQuery('');
                      setSelectedSite(null);
                      setSelectedShiftId(null);
                      
                      // Reset display to show all sites and all guards
                      setDisplayedOfficeLocations(officeLocations);
                      setDisplayedGuardLocations(guardLocations);
                      
                      // DON'T clear geofences - they should remain visible
                    }}
                    onShiftSelected={handleShiftSelected} // Pass shift selection callback
                    onEmployeesForMap={handleEmployeesForMap} // Pass employees with locations for map
                    onEmployeeClick={handleEmployeeClick} // Pass employee card click handler
                  />
                )}
              </motion.div>
            )}

          {isTrackRidesPanelOpen && (
            <motion.div
              key="track-rides-panel"
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              transition={{ duration: 0.3 }}
              className="flex-shrink-0" // TrackRidesComponent handles its own width/height
            >
              <TrackRidesComponent
                liveRides={liveRides}
                officeLocations={officeLocations} // Pass API-fetched office locations
                onDispatchNewRide={handleDispatchNewRide}
                onClose={() => {
                  setIsTrackRidesPanelOpen(false);
                  setSelectedRideForMap(null); // Clear selected ride when panel closes
                }}
                onSelectRideForMap={setSelectedRideForMap}
                selectedRideIdForMap={selectedRideForMap}
                onShowEmployeeInfo={handleShowEmployeeInfoTable} // <--- PASS CALLBACK TO TRACK RIDES
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Modals */}
      <OfficeModal
        isOpen={isOfficeModalOpen}
        onClose={() => setIsOfficeModalOpen(false)}
        office={currentOffice}
        onSave={handleSaveOffice}
      />

      <EmployeeModal
        isOpen={isEmployeeModalOpen}
        onClose={() => setIsEmployeeModalOpen(false)}
        employee={currentEmployee}
        onSave={handleSaveEmployee}
      />

      <AbsentShiftModal
        isOpen={isAbsentShiftModalOpen}
        onClose={() => setIsAbsentShiftModalOpen(false)}
        shift={currentAbsentShift}
        onSave={handleSaveAbsentShift}
      />

      {/* Removed Current Stats Edit Modal entirely */}

      {/* Add New Site Modal (controlled by Dashboard.js) */}
      {isAddSiteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className={combineClasses(
              'bg-white/90 backdrop-blur-md rounded-xl border border-white/30 shadow-lg p-6 w-full max-w-md space-y-4'
            )}
          >
            <h2 className="text-2xl font-bold text-green-700">Add New Site</h2>
            <p className="text-gray-700">
              This is a placeholder for your &quot;Add New Site&quot; form.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                onClick={() => setIsAddSiteModalOpen(false)}
                className="bg-gray-500/20 text-gray-700 hover:bg-gray-500/30"
              >
                Close
              </Button>
              {/* Add your form fields and save button here */}
            </div>
          </motion.div>
        </div>
      )}

      {/* Employee Info Table Conditional Render */}
      <AnimatePresence>
        {showEmployeeInfoTable && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-center justify-center p-1 md:p-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <EmployeeInfoTable
              employees={employeeTableData} // Use employeeTableData here
              onClose={handleCloseEmployeeInfoTable}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Removed Test API Modal Conditional Render */}
    </div>
  );
};

export default Dashboard;
